import React, { useState, useMemo } from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
} from 'react-native';
import { IEvent, CALENDAR_KEYS } from '@/types/admin/events';
import { formatEventType } from '@/hooks/admin/useEvents';
import { EventInfoTab } from './EventInfoTab';
import { EventAttendanceTab } from './AttendanceTab';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { TabSegment } from '@/components/shared/redesign';

interface EventDetailsModalProps {
  visible: boolean;
  event: IEvent | null;
  onClose: () => void;
  onEdit?: (eventId: number) => void;
}

type TabType = 'info' | 'attendance';

export const TABS: { key: TabType; label: string }[] = [
  { key: 'info', label: 'Event Info' },
  { key: 'attendance', label: 'Attendees' },
];

export const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
  visible,
  event,
  onClose,
  onEdit,
}) => {
  const { redesign } = useAdminColors();
  const [activeTab, setActiveTab] = useState<TabType>('info');

  const eventType = useMemo(() => {
    if (!event) return null;
    return formatEventType(event);
  }, [event]);

  const eventKey = useMemo(() => {
    if (!eventType) return null;
    return CALENDAR_KEYS.find((key) => key.title === eventType);
  }, [eventType]);

  if (!event) return null;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'info':
        return (
          <AdminErrorBoundary context="EventInfoTab">
            <EventInfoTab event={event} />
          </AdminErrorBoundary>
        );
      case 'attendance':
        return (
          <AdminErrorBoundary context="EventAttendanceTab">
            <EventAttendanceTab event={event} />
          </AdminErrorBoundary>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SafeAreaView style={[styles.container, { backgroundColor: redesign.bg.base }]}>
        {/* Header */}
        <View
          style={[
            styles.header,
            { borderBottomColor: redesign.card.border },
          ]}
        >
          <View style={styles.headerLeft}>
            <Text style={[styles.eventTitle, { color: redesign.text.primary }]} numberOfLines={2}>
              {event.name}
            </Text>
            <View style={styles.eventStatus}>
              <View
                style={[
                  styles.statusDot,
                  { backgroundColor: eventKey?.color || redesign.text.muted },
                ]}
              />
              <Text style={[styles.statusText, { color: redesign.text.muted }]}>
                {eventType}
              </Text>
            </View>
          </View>
          <View style={styles.headerButtons}>
            {onEdit && (
              <TouchableOpacity
                style={[
                  styles.editButton,
                  { backgroundColor: redesign.glass.buttonBg },
                ]}
                onPress={() => onEdit(event.id)}
                accessibilityLabel="Edit event"
                accessibilityHint="Tap to edit this event"
                accessibilityRole="button"
              >
                <SVG_COMPONENTS.EDIT_DOT
                  width={18}
                  height={18}
                  color={redesign.text.primary}
                />
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[
                styles.closeButton,
                { backgroundColor: redesign.glass.buttonBg },
              ]}
              onPress={onClose}
              accessibilityLabel="Close event details"
              accessibilityHint="Tap to close the event details modal"
              accessibilityRole="button"
            >
              <SVG_COMPONENTS.CANCEL_X
                width={14}
                height={14}
                color={redesign.text.primary}
              />
            </TouchableOpacity>
          </View>
        </View>

        {/* Tabs */}
        <View style={styles.tabsContainer}>
          <TabSegment
            items={TABS.map((t) => ({ key: t.key, label: t.label }))}
            selectedKey={activeTab}
            onChange={(key) => setActiveTab(key as TabType)}
            variant="underline"
            fullWidth
          />
        </View>

        {/* Content */}
        <View style={styles.content}>{renderTabContent()}</View>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 20,
    borderBottomWidth: 1,
    marginBottom: 10,
  },
  headerLeft: {
    flex: 1,
    marginRight: 16,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  editButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  eventTitle: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 8,
  },
  eventStatus: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '500',
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabsContainer: {
    marginBottom: 20,
  },
  content: {
    flex: 1,
  },
});
